import { useMemo } from "react";
import type { GroupMemberInMonth } from "@/api/nurses";
import type {
  InboundInfo,
  InboundItem,
} from "@/hooks/roster-craete/useRosterSchedule";
import {
  computeAssignmentStarts,
  inboundItemsFromInboundField,
} from "@/services/assignment";
import type {
  CopyNurses,
  FullNurseSchemaWithPreceptor,
} from "@/utils/rosterCreate-util";
import { normalizeScheduleCellCode } from "@/utils/shift-util";

const EMPTY_SCHEDULE_CODES = new Set(["", "-", "주"]);

type RosterCreateVisibleNurse = {
  readonly nurse_id: string;
  readonly schedule?: readonly unknown[] | null;
  readonly assignments?: readonly InboundItem[] | null;
  readonly inbound?: InboundInfo | readonly InboundItem[] | null;
  readonly is_inbound?: boolean | null;
};

function hasMonthlyScheduleCell(nurse: RosterCreateVisibleNurse): boolean {
  return (
    nurse.schedule?.some((cell) => {
      const code = normalizeScheduleCellCode(cell).trim();
      return !EMPTY_SCHEDULE_CODES.has(code);
    }) ?? false
  );
}

function hasMonthlyAssignment(
  nurse: RosterCreateVisibleNurse,
  year?: number | null,
  month?: number | null
): boolean {
  if (!year || !month) return false;
  const inboundItems = inboundItemsFromInboundField(nurse.inbound ?? undefined);
  const assignmentItems =
    inboundItems.length > 0 ? inboundItems : [...(nurse.assignments ?? [])];
  return computeAssignmentStarts(assignmentItems, year, month).length > 0;
}

function isInboundMonthlyMember(member: GroupMemberInMonth): boolean {
  return member.membership_status === "inbound";
}

function isInboundRosterRow(nurse: RosterCreateVisibleNurse): boolean {
  return nurse.is_inbound === true || inboundItemsFromInboundField(
    nurse.inbound ?? undefined
  ).length > 0;
}

export function filterRosterCreateVisibleNurses<
  TNurse extends RosterCreateVisibleNurse,
>(
  copyNurses: TNurse[],
  memberStatusMap?: ReadonlyMap<string, GroupMemberInMonth>,
  year?: number | null,
  month?: number | null
): TNurse[] {
  if (!memberStatusMap) return copyNurses;

  return copyNurses.filter((nurse) => {
    const member = memberStatusMap.get(nurse.nurse_id);
    if (member?.membership_status === "active") return true;
    if (member && isInboundMonthlyMember(member)) {
      return hasMonthlyAssignment(nurse, year, month);
    }
    if (!member && isInboundRosterRow(nurse)) {
      return (
        hasMonthlyScheduleCell(nurse) || hasMonthlyAssignment(nurse, year, month)
      );
    }
    return (
      hasMonthlyScheduleCell(nurse) || hasMonthlyAssignment(nurse, year, month)
    );
  });
}

export function applyRosterCreateMonthlyMemberAttributes(
  copyNurses: CopyNurses,
  memberStatusMap?: ReadonlyMap<string, GroupMemberInMonth>
): CopyNurses {
  if (!memberStatusMap) return copyNurses;

  return copyNurses.map((nurse) => {
    const member = memberStatusMap.get(nurse.nurse_id);
    if (!member) return nurse;

    const grade = member.as_of_grade ?? nurse.grade;
    const teamId = member.as_of_team ?? nurse.team_id;
    if (grade === nurse.grade && teamId === nurse.team_id) return nurse;

    return {
      ...nurse,
      grade,
      team_id: teamId,
    };
  });
}

function buildSyntheticInboundRosterRow(
  member: GroupMemberInMonth,
  profile: FullNurseSchemaWithPreceptor,
  groupId: string | null | undefined,
  daysInMonth: number
): CopyNurses[number] {
  return {
    ...profile,
    account_id: profile.account_id ?? "",
    active: 1,
    age: profile.age ?? null,
    allowed_shifts: profile.allowed_shifts ?? [],
    counts: { D: 0, E: 0, N: 0, O: 0, M: 0 },
    enable_aide: profile.enable_aide ?? false,
    enable_nurse_pair_preference: profile.enable_nurse_pair_preference ?? false,
    emp_num: profile.emp_num ?? "",
    experience: profile.experience ?? 0,
    fixed_shift: profile.fixed_shift ?? null,
    gender: profile.gender ?? null,
    grade: member.as_of_grade,
    group_id: groupId ?? "",
    inbound: profile.inbound,
    is_head_nurse: profile.is_head_nurse ?? false,
    is_inbound: true,
    is_weekend_off: profile.is_weekend_off ?? false,
    joining_date: profile.joining_date ?? null,
    level_: profile.level_ ?? "일반",
    name: member.name ?? member.nurse_id,
    nurse_id: member.nurse_id,
    nurse_memo: profile.nurse_memo ?? null,
    office_id: profile.office_id ?? "",
    personal_off_adjustment: profile.personal_off_adjustment ?? 0,
    preceptor: profile.preceptor ?? null,
    preceptor_id: profile.preceptor_id ?? null,
    resignation_date: profile.resignation_date ?? null,
    resignation_reason: profile.resignation_reason ?? null,
    resignation_reason_memo: profile.resignation_reason_memo ?? null,
    role: profile.role ?? "RN",
    schedule: Array.from({ length: daysInMonth }, () => "-"),
    sequence: profile.sequence ?? 0,
    team_id: member.as_of_team,
    weekly_off_weekday: profile.weekly_off_weekday ?? null,
    wanted_max_requests: profile.wanted_max_requests ?? null,
  };
}

export function appendRosterCreateMonthlyInboundMembers(
  copyNurses: CopyNurses,
  memberStatusMap?: ReadonlyMap<string, GroupMemberInMonth>,
  profileNurses?: readonly FullNurseSchemaWithPreceptor[],
  groupId?: string | null,
  year?: number | null,
  month?: number | null
): CopyNurses {
  if (!memberStatusMap || !year || !month) return copyNurses;

  const present = new Set(copyNurses.map((nurse) => nurse.nurse_id));
  const profileById = new Map(
    (profileNurses ?? []).map((profile) => [profile.nurse_id, profile] as const)
  );
  const daysInMonth = new Date(year, month, 0).getDate();
  const syntheticRows: CopyNurses = [];

  memberStatusMap.forEach((member) => {
    if (present.has(member.nurse_id) || !isInboundMonthlyMember(member)) return;
    const profile = profileById.get(member.nurse_id);
    if (!profile || !hasMonthlyAssignment(profile, year, month)) return;
    syntheticRows.push(
      buildSyntheticInboundRosterRow(member, profile, groupId, daysInMonth)
    );
  });

  if (syntheticRows.length === 0) return copyNurses;
  return [...copyNurses, ...syntheticRows];
}

type UseRosterCreateVisibleNursesParams = {
  readonly copyNurses: CopyNurses;
  readonly memberStatusMap?: ReadonlyMap<string, GroupMemberInMonth>;
  readonly profileNurses?: readonly FullNurseSchemaWithPreceptor[];
  readonly groupId?: string | null;
  readonly year?: number | null;
  readonly month?: number | null;
};

export function useRosterCreateVisibleNurses({
  copyNurses,
  memberStatusMap,
  profileNurses,
  groupId,
  year,
  month,
}: UseRosterCreateVisibleNursesParams): CopyNurses {
  return useMemo(() => {
    const withMonthlyMemberAttributes =
      applyRosterCreateMonthlyMemberAttributes(copyNurses, memberStatusMap);
    const withMonthlyInboundRows = appendRosterCreateMonthlyInboundMembers(
      withMonthlyMemberAttributes,
      memberStatusMap,
      profileNurses,
      groupId,
      year,
      month
    );
    return filterRosterCreateVisibleNurses(
      withMonthlyInboundRows,
      memberStatusMap,
      year,
      month
    );
  }, [copyNurses, groupId, memberStatusMap, month, profileNurses, year]);
}
